// src/components/onboarding/FoxOnboarding2.jsx
//
// Surf v2 onboarding — pillar-based discovery with talk-track approval +
// activation ritual. Spec: strategic-planning/SURF-V2-ARCHITECTURE.md.
//
// Lives at /onboarding-v2 in parallel with v1's /onboarding. Once v2 is
// validated in production we'll swap the routes and delete v1.
//
// Key v1 → v2 deltas:
//   - Right panel is a PillarStack (7 pillars with status/confidence/data),
//     not a flat Notes list
//   - Scrape-preview fires the moment website lands in the pillar data;
//     digest gets folded back into every subsequent /v2/chat call
//   - When complete=true fires, we enter the approval phase: Sonnet drafts
//     a full talk track (strategy + objectives + sample opener + sample
//     qualification flow), tenant approves or refines in plain English
//   - When the tenant clicks Approve, the activation ritual plays while
//     /v2/finalize provisions in the background; then we land on /dashboard

import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Send, Edit2, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import { fetchWithCsrf, getCsrfToken } from '../../lib/fetchWithCsrf';
import supabase from '../../lib/supabaseClient';
import A2PCaptureStep from './A2PCaptureStep';

// A2P bypass list (tester accounts) — shared with the Express /v2/finalize half.
import { isA2PBypassEmail } from '../../lib/a2pBypassEmails';
// ─── Constants ──────────────────────────────────────────────────────────────

const PILLAR_KEYS = [
  'business_operations',
  'industry_context',
  'audience',
  'qualification',
  'voice_guardrails',
  'objections',
  'conversion_goal',
];

const PILLAR_DISPLAY = {
  business_operations: 'Operations',
  industry_context:    'Industry',
  audience:            'Audience',
  qualification:       'Qualification',
  voice_guardrails:    'Voice & Guardrails',
  objections:          'Objections',
  conversion_goal:     'Conversion Goal',
};

// Default empty pillar shape — matches the v2 schema exactly so React's
// equality checks and per-pillar status comparisons work without nullable
// branching. Mirrors src/api_routes/onboardingDemo.js EXTRACTION_SCHEMA_V2.
const DEFAULT_PILLAR_SHAPE = {
  business_operations: { status: 'not_started', confidence: 0, data: {
    company_name: '', contact_first_name: '', website: '', industry: '', what_they_sell: '',
    differentiators: [], lead_handler_today: '', sales_cycle_rough: '',
    business_age: '', business_size: '', hours_of_operation: '',
    geographic_coverage: '', area_code: '',
  }},
  industry_context: { status: 'not_started', confidence: 0, data: {
    industry_vocabulary: [], industry_landmines: [], buying_signals: [],
    regulatory_considerations: '',
  }},
  audience: { status: 'not_started', confidence: 0, data: {
    icp_description: '', acquisition_channel: '', awareness_level: '',
    lead_motivation: '', known_intake_fields: [], audience_sub_segments: [],
  }},
  qualification: { status: 'not_started', confidence: 0, data: {
    hard_requirements: [], fit_signals: [], disqualifiers: [], qualifying_questions: [],
    escalation_trigger: '', mismatch_handling: '',
  }},
  voice_guardrails: { status: 'not_started', confidence: 0, data: {
    ai_name: '', tone: null, persona: null, brand_voice_descriptors: [],
    custom_guardrails: [], stylistic_notes: '',
  }},
  objections: { status: 'not_started', confidence: 0, data: {
    top_objections: [], pricing_objection: '', timing_objection: '',
    skepticism_objection: '',
  }},
  conversion_goal: { status: 'not_started', confidence: 0, data: {
    primary_goal: '', escalation_trigger: '', success_criteria: '',
    meeting_modality: '',
    booking_url: '', booking_prompt: '',
  }},
};

const HARDCODED_OPENER =
  "Hey, I'm Surf. I'll get your AI ready by asking you a bunch about your business. To kick things off, what's your company called?";

// localStorage key for crash/refresh recovery of an in-progress conversation.
// A founder who hits a blip (the org-credit hiccup that surfaced this, a stray
// reload, flaky wifi) resumes instead of restarting 25 minutes of setup.
const DRAFT_KEY = 'surf_onboarding_v2_draft';

const CHAR_STREAM_MS = 22;
const TYPING_BEFORE_MS = 750;
const READING_DWELL_MS = 2000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Root component ─────────────────────────────────────────────────────────

export default function FoxOnboarding2() {
  const navigate = useNavigate();

  // Phase: intro → conversation → approval → a2p_handoff → a2p → activation
  // After the tenant approves their talk track, Surf says one line in the chat
  // ('a2p_handoff', no overlay) then the A2P card slides up ('a2p'). The 'a2p'
  // phase is a REQUIRED carrier-registration gate: the tenant submits their
  // business identity (subaccount + customer profile) before we finalize and
  // let them into the platform. See A2PCaptureStep.
  const [phase, setPhase] = useState('intro');

  // Conversation state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isFoxTyping, setIsFoxTyping] = useState(false);
  const [foxState, setFoxState] = useState('idle'); // idle | thinking | speaking | distracted

  // Pillar state — driven entirely by Claude's extracted JSON each turn
  const [pillars, setPillars] = useState(DEFAULT_PILLAR_SHAPE);
  const [currentPillar, setCurrentPillar] = useState('business_operations');
  const [expandedPillar, setExpandedPillar] = useState('business_operations');

  // Scrape state
  const [websiteIntel, setWebsiteIntel] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState('idle'); // idle | scraping | done | failed

  // Approval state
  const [talkTrackDraft, setTalkTrackDraft] = useState(null);
  const [talkTrackLoading, setTalkTrackLoading] = useState(false);
  const [talkTrackError, setTalkTrackError] = useState(null);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [feedbackLog, setFeedbackLog] = useState([]);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Activation state
  const [activationError, setActivationError] = useState(null);
  const [phoneRetryMode, setPhoneRetryMode] = useState(false);

  // A2P hand-off: after Surf's transition line finishes typing we show a "Let's
  // go" button instead of auto-advancing, so the tenant reads at their own pace.
  const [a2pHandoffReady, setA2pHandoffReady] = useState(false);

  // Auth / guard
  const [tenantId, setTenantId] = useState(null);
  const [guardChecked, setGuardChecked] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [pendingTestExtracted, setPendingTestExtracted] = useState(null);

  // Holds the message history of a turn that failed so the user can re-fire it
  // from a Retry button WITHOUT re-typing (and without duplicating their message).
  const [retryHistory, setRetryHistory] = useState(null);

  // Refs
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const startedRef = useRef(false);
  const startedAtRef = useRef(null);
  const messagesRef = useRef([]);
  const lastScrapedUrlRef = useRef(null);
  const finalizeFiredRef = useRef(false);
  // Once the A2P card has submitted the customer profile, re-approving the talk
  // track (e.g. after an area-code finalize retry sends us back to 'approval')
  // must NOT re-show the card — the profile already exists (would 409).
  const a2pSubmittedRef = useRef(false);
  // Bounds the finalize-gate backstop so a stubborn cross-pillar verdict can't
  // bounce the tenant between conversation and approval forever.
  const finalizeGateRetriesRef = useRef(0);

  // ─── Quality-judge plumbing (headless; invisible to the tenant) ────────────
  // Mirror of pillars for synchronous prev/next status diffing inside async
  // chains (the `pillars` state closure goes stale across awaits).
  const pillarsRef = useRef(DEFAULT_PILLAR_SHAPE);
  // Per-pillar judge ledger: { [pillarKey]: { state: 'pending'|'pass'|'needs_clarity', directive } }.
  // A pillar carries a verdict only while it is locked; reopening clears it so it
  // gets re-judged on the next lock. This ref is the source of truth for the
  // completion barrier and for pending_critique injection.
  const judgeLedgerRef = useRef({});
  // In-flight judge promises so the completion barrier can await them.
  const judgePromisesRef = useRef({});
  // Append-only log of every judge verdict (incl. re-judges after a reopen), sent
  // to finalize as the bounce/reopen portion of the QC receipt.
  const qcHistoryRef = useRef([]);

  // Keep pillarsRef synced for non-chat paths (draft resume, test discard). The
  // chat turn also sets it manually before any await so diffs are race-free.
  useEffect(() => { pillarsRef.current = pillars; }, [pillars]);

  // Pre-warm on mount: prime the CSRF token AND warm the API instance before the
  // tenant's first answer, so turn 1 doesn't pay cold-start latency (a cold Render
  // instance was the original turn-1 "squirrel"). Fire-and-forget.
  useEffect(() => { getCsrfToken().catch(() => {}); }, []);

  // canSend during conversation
  const canSend =
    phase === 'conversation' &&
    foxState === 'idle' &&
    !isFoxTyping &&
    !pendingTestExtracted;

  // Keep messagesRef in lockstep with state so async chains see fresh data
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
    messagesRef.current = messages;
  }, [messages, isFoxTyping]);

  useEffect(() => {
    if (canSend && inputRef.current) inputRef.current.focus();
  }, [canSend]);

  // ─── Autosave the in-progress conversation (crash / refresh recovery) ──────
  // Keyed by tenant so a different login in the same browser can't inherit it.
  // Only saves once past the opener; cleared when the conversation completes.
  useEffect(() => {
    if (phase !== 'conversation' || !tenantId) return;
    // Persist only real conversation turns, never the transient status bubbles.
    const saveMessages = (messages || []).filter((m) => !m.transient);
    if (saveMessages.length <= 1) return;
    // Don't save mid-stream: avoids a localStorage write per character and keeps
    // us from persisting a half-typed assistant message. The final setMessages
    // (streaming: false) re-fires this effect and saves the clean state.
    const last = saveMessages[saveMessages.length - 1];
    if (isFoxTyping || last?.streaming) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        tenantId,
        messages: saveMessages,
        pillars,
        currentPillar,
        startedAt: startedAtRef.current,
        savedAt: Date.now(),
      }));
    } catch (_) { /* storage full / disabled — non-fatal */ }
    // Cross-device resume: also persist server-side (best-effort, fire-and-forget).
    // localStorage covers same-device; this lets them resume on a different device.
    fetchWithCsrf('/api/onboarding/v2/save-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: saveMessages,
        pillars,
        current_pillar: currentPillar,
        started_at: startedAtRef.current || null,
      }),
    }).catch(() => { /* non-fatal */ });
  }, [messages, isFoxTyping, pillars, currentPillar, phase, tenantId]);

  // ─── Resume routing ───────────────────────────────────────────────────────
  const allSevenLocked = (p) => !!p && PILLAR_KEYS.every((k) => p[k]?.status === 'locked');

  // When a restored draft was already complete (all 7 locked), resume at the
  // talk-track APPROVAL step — not a dead finished conversation. Fires once the
  // restored pillars are in state so the regen uses fresh data.
  const [pendingResumeApproval, setPendingResumeApproval] = useState(false);
  useEffect(() => {
    if (pendingResumeApproval && allSevenLocked(pillars)) {
      setPendingResumeApproval(false);
      enterApprovalPhase(pillars);
    }
  }, [pendingResumeApproval, pillars]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply a restored draft (local or server) and route: approval if the interview
  // was already complete, otherwise back into the conversation where they left off.
  function applyRestoredDraft({ messages: msgs, pillars: plrs, currentPillar: curPillar, startedAt }) {
    startedRef.current = true;
    startedAtRef.current = startedAt || new Date().toISOString();
    setMessages(msgs);
    if (plrs) setPillars(plrs);
    if (curPillar) { setCurrentPillar(curPillar); setExpandedPillar(curPillar); }
    if (allSevenLocked(plrs)) {
      setPhase('approval');
      setTalkTrackLoading(true);
      setPendingResumeApproval(true);
    } else {
      setPhase('conversation');
      const last = msgs[msgs.length - 1];
      if (last && last.role === 'user') setRetryHistory(msgs);
    }
  }

  // ─── Guard check on mount ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function checkGuard() {
      try {
        const { data: { user }, error: userErr } = await supabase.auth.getUser();
        if (cancelled) return;
        if (userErr || !user) {
          // Onboarding is a public route (it sits outside OnboardingGuard to avoid
          // a redirect loop), so it must self-guard: a logged-out visitor gets sent
          // to login instead of landing on an intro they can't actually use.
          navigate('/login', { replace: true });
          return;
        }
        const { data: profile } = await supabase
          .from('users_profile')
          .select('tenant_id, role')
          .eq('id', user.id)
          .single();
        if (cancelled) return;

        if (profile?.tenant_id) setTenantId(profile.tenant_id);

        // Resume a saved in-progress conversation for THIS tenant BEFORE any early
        // return (admin/test-mode or onboarding-complete), so a refresh restores the
        // draft for EVERY user, not just non-admins. Drops straight back into the chat
        // where they left off; if the last turn was an unanswered user message, surface
        // Retry so they finish without re-typing.
        let restoredFromLocal = false;
        try {
          const raw = localStorage.getItem(DRAFT_KEY);
          if (raw) {
            const draft = JSON.parse(raw);
            if (draft && draft.tenantId === profile?.tenant_id &&
                Array.isArray(draft.messages) && draft.messages.length > 1) {
              applyRestoredDraft({
                messages: draft.messages, pillars: draft.pillars,
                currentPillar: draft.currentPillar, startedAt: draft.startedAt,
              });
              restoredFromLocal = true;
            }
          }
        } catch (_) { /* corrupt draft — ignore, start fresh */ }

        // No same-device draft? Fall back to the SERVER draft so they can resume
        // on a fresh device / browser (the "I save your spot" promise, unconditional).
        if (!restoredFromLocal) {
          try {
            const res = await fetchWithCsrf('/api/onboarding/v2/draft');
            if (res.ok) {
              const { draft } = await res.json();
              if (!cancelled && draft && Array.isArray(draft.messages) && draft.messages.length > 1) {
                applyRestoredDraft({
                  messages: draft.messages, pillars: draft.pillars,
                  currentPillar: draft.current_pillar, startedAt: draft.started_at,
                });
              }
            }
          } catch (_) { /* no server draft — start fresh */ }
        }

        const isAdmin = profile?.role === 'global_admin';
        if (isAdmin) {
          setTestMode(true);
          setGuardChecked(true);
          return;
        }

        if (profile?.tenant_id) {
          const { data: tenant } = await supabase
            .from('tenants')
            .select('onboarding_complete')
            .eq('id', profile.tenant_id)
            .single();
          if (cancelled) return;
          if (tenant?.onboarding_complete) {
            navigate('/dashboard', { replace: true });
            return;
          }
        }

        setGuardChecked(true);
      } catch (e) {
        console.error('Onboarding v2 guard check failed', e);
        if (!cancelled) setGuardChecked(true);
      }
    }
    checkGuard();
    return () => { cancelled = true; };
  }, [navigate]);

  // ─── Seed the hardcoded opener once we enter conversation phase ──────────
  useEffect(() => {
    if (phase !== 'conversation') return;
    if (startedRef.current) return;
    startedRef.current = true;
    startedAtRef.current = new Date().toISOString();
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, raw: null, streaming: false }]);
  }, [phase]);

  // ─── Scrape kickoff: fires the moment a website lands in the pillar data
  // and we haven't already kicked off (or completed) a scrape. Fully async,
  // never blocks the conversation. ────────────────────────────────────────
  useEffect(() => {
    const captured = pillars.business_operations?.data?.website;
    if (!captured) return;

    let normalized = captured.trim();
    if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
    if (lastScrapedUrlRef.current === normalized) return;
    lastScrapedUrlRef.current = normalized;
    setWebsiteUrl(normalized);
    setWebsiteIntel(null);
    setScrapeStatus('scraping');

    // Fire the A2P consent pre-scan in parallel (fire-and-forget) so the later
    // A2P capture step can show findings INSTANTLY from cache instead of waiting
    // on a live fetch + LLM scan. Never blocks the conversation.
    supabase.functions.invoke('verify-sms-consent', {
      body: { website: normalized, business_name: pillars.business_operations?.data?.company_name || '' },
    }).catch(() => { /* best-effort pre-scan */ });

    fetchWithCsrf('/api/onboarding/scrape-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: normalized }),
    })
      .then((res) => res.json())
      .then((body) => {
        if (body?.ok && body.digest) {
          setWebsiteIntel(body.digest);
          setScrapeStatus('done');
        } else {
          console.warn('[fox v2] scrape-preview returned not-ok', body);
          setScrapeStatus('failed');
        }
      })
      .catch((err) => {
        console.error('[fox v2] scrape-preview threw', err);
        setScrapeStatus('failed');
      });
  }, [pillars.business_operations?.data?.website]);

  // ─── Streaming helper ─────────────────────────────────────────────────────
  async function streamAssistant(text, raw = null, opts = {}) {
    const { speakingState = 'speaking', endState = 'idle', transient = false } = opts;
    setFoxState('thinking');
    setIsFoxTyping(true);
    await sleep(TYPING_BEFORE_MS);
    setIsFoxTyping(false);
    setFoxState(speakingState);
    // `transient` marks UI-only status bubbles (the "squirrel" / error notices) so
    // they're excluded from the API history and the recovery draft.
    setMessages((prev) => [...prev, { role: 'assistant', text: '', raw, streaming: true, transient }]);
    for (let i = 1; i <= text.length; i++) {
      await sleep(CHAR_STREAM_MS);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], text: text.slice(0, i) };
        return next;
      });
    }
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], streaming: false };
      return next;
    });
    setFoxState(endState);
  }

  // ─── Quality-judge helpers ────────────────────────────────────────────────
  // The transcript the judge reads (Surf + tenant words, no client bookkeeping).
  function transcriptForJudge() {
    return (messagesRef.current || [])
      .filter((m) => !m.transient)
      .map((m) => ({ role: m.role, content: m.text || '' }))
      .filter((m) => m.content);
  }

  // Outstanding critiques to replay to Surf on the next chat turn.
  function gatherCritique() {
    return Object.entries(judgeLedgerRef.current)
      .filter(([, v]) => v && v.state === 'needs_clarity' && v.directive)
      .map(([pillar, v]) => ({ pillar, directive: v.directive }));
  }

  // Fire-and-forget per-pillar judge. Runs DURING the tenant's read/type time, so
  // it never sits in the critical path. Fails open (treats errors as pass) — the
  // server-authoritative finalize gate is the real backstop.
  async function callJudgeApi(pillarKey, pillarData, messages) {
    try {
      const res = await fetchWithCsrf('/api/onboarding/v2/judge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pillar_key: pillarKey, pillar_data: pillarData, messages }),
      });
      if (!res.ok) return { verdict: 'pass', directive: '' };
      return await res.json();
    } catch (_) {
      return { verdict: 'pass', directive: '' };
    }
  }

  // For every pillar that just transitioned into "locked", fire its judge in the
  // background and record the in-flight promise so the completion barrier can wait.
  function fireJudgesForNewLocks(prevPillars, nextPillars) {
    if (!nextPillars) return;
    const transcript = transcriptForJudge();
    for (const k of PILLAR_KEYS) {
      const wasLocked = prevPillars?.[k]?.status === 'locked';
      const nowLocked = nextPillars?.[k]?.status === 'locked';
      if (nowLocked && !wasLocked) {
        judgeLedgerRef.current[k] = { state: 'pending', directive: '' };
        const p = callJudgeApi(k, nextPillars[k].data, transcript)
          .then((v) => {
            // Receipt: log every verdict, including re-judges after a reopen.
            qcHistoryRef.current.push({ pillar: k, verdict: v.verdict, gaps: (v.gaps || []).length });
            // Only record in the live ledger if the pillar is still locked.
            if (pillarsRef.current?.[k]?.status === 'locked') {
              judgeLedgerRef.current[k] = {
                state: v.verdict === 'needs_clarity' ? 'needs_clarity' : 'pass',
                directive: v.directive || '',
              };
            }
          })
          .finally(() => { delete judgePromisesRef.current[k]; });
        judgePromisesRef.current[k] = p;
      }
    }
  }

  // A pillar that left "locked" (Surf reopened it) drops its verdict; it will be
  // re-judged when it locks again.
  function reconcileReopened(prevPillars, nextPillars) {
    if (!nextPillars) return;
    for (const k of PILLAR_KEYS) {
      if (nextPillars[k]?.status !== 'locked' && judgeLedgerRef.current[k]) {
        delete judgeLedgerRef.current[k];
      }
    }
  }

  // Completion barrier: wait for any in-flight judges to land before deciding
  // whether the interview is truly done.
  async function settlePendingJudges() {
    const pending = Object.values(judgePromisesRef.current);
    if (pending.length) await Promise.allSettled(pending);
  }

  // History trimmed to end at the last tenant message, so the Messages API has a
  // user-last turn to respond to (used by the finalize-gate backstop reopen).
  function historyThroughLastUser() {
    const msgs = (messagesRef.current || []).filter((m) => !m.transient);
    let i = msgs.length - 1;
    for (; i >= 0; i--) if (msgs[i].role === 'user') break;
    return i >= 0 ? msgs.slice(0, i + 1) : msgs;
  }

  // ─── API helpers ──────────────────────────────────────────────────────────
  async function callChatApi(historyForApi, extraCritique = []) {
    const baseMessages = historyForApi.map((m) => ({
      role: m.role,
      content: m.role === 'assistant' ? (m.raw || m.text) : m.text,
    }));
    // The hardcoded opener is the first assistant turn locally; the Messages
    // API needs a user-first turn, so prepend a synthetic kickoff.
    const apiMessages = baseMessages.length > 0 && baseMessages[0].role === 'assistant'
      ? [{ role: 'user', content: 'Begin onboarding.' }, ...baseMessages]
      : baseMessages;
    // Replay any outstanding judge critiques so Surf reopens the gap. Stays
    // attached every turn until the pillar is reopened (which clears the entry).
    const pendingCritique = [...gatherCritique(), ...extraCritique];
    const res = await fetchWithCsrf('/api/onboarding/v2/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: apiMessages,
        website_intel: websiteIntel || undefined,
        website_url: websiteUrl || undefined,
        scrape_failed: scrapeStatus === 'failed' || undefined,
        pending_critique: pendingCritique.length ? pendingCritique : undefined,
      }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`v2/chat failed: ${res.status} ${detail.slice(0, 200)}`);
    }
    return res.json();
  }

  async function callRegenerateTalkTrack(feedback, pillarsOverride) {
    const body = {
      pillars: pillarsOverride || pillars,
      website_intel: websiteIntel || undefined,
    };
    if (feedback && talkTrackDraft) {
      body.tenant_feedback = feedback;
      body.current_strategy = talkTrackDraft.strategy_block;
      body.current_objectives = talkTrackDraft.objectives_block;
      body.current_opener = talkTrackDraft.sample_opener;
      body.current_flow = talkTrackDraft.sample_qualification_flow;
    }
    const res = await fetchWithCsrf('/api/onboarding/v2/regenerate-talk-track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body: data };
  }

  async function callFinalizeApi(extraOverrides = {}) {
    const transcriptMessages = (messagesRef.current || []).map((m) => ({
      role: m.role, text: m.text, raw: m.raw || null,
    }));
    const res = await fetchWithCsrf('/api/onboarding/v2/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pillars,
        approved_talk_track: talkTrackDraft,
        feedback_log: feedbackLog,
        messages: transcriptMessages,
        started_at: startedAtRef.current || null,
        website_intel: websiteIntel || undefined,
        qc_ledger: qcHistoryRef.current,
        ...extraOverrides,
      }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body: data };
  }

  // ─── One conversation turn ────────────────────────────────────────────────
  async function runChatTurn(messageHistory) {
    setRetryHistory(null); // a new attempt supersedes any pending retry
    setFoxState('thinking');
    setIsFoxTyping(true);

    let chatResp;
    try {
      chatResp = await callChatApi(messageHistory);
    } catch (err) {
      console.error('chat failed, retrying silently once', err);
      // Silent quick retry — NO "squirrel" message. The server already retries
      // transient model/API hiccups internally, so a client-level failure here is
      // usually a one-off network/proxy blip. Recover invisibly (the fox just stays
      // "thinking" a beat longer) to protect the first impression.
      await sleep(600);
      try {
        chatResp = await callChatApi(messageHistory);
      } catch (err2) {
        console.error('chat retry also failed', err2);
        // Your progress is autosaved, so this is recoverable. Offer Retry (re-fires
        // this exact turn, no re-typing, no duplicate) and tell them NOT to refresh
        // away their work. The old copy told them to refresh, which wiped everything.
        await streamAssistant(
          "Hit a snag on my end. Your progress is saved, so don't worry. Tap Retry to pick up right where we left off.",
          null,
          { transient: true }
        );
        setRetryHistory(messageHistory);
        setFoxState('idle');
        setIsFoxTyping(false);
        return;
      }
    }

    const { message, extracted, complete } = chatResp;

    // Apply pillar state BEFORE streaming so judge diffs run against fresh data.
    const prevPillars = pillarsRef.current;
    const nextPillars = extracted?.pillars || null;
    if (nextPillars) {
      pillarsRef.current = nextPillars;
      setPillars(nextPillars);
      if (extracted.current_pillar && extracted.current_pillar !== currentPillar) {
        setCurrentPillar(extracted.current_pillar);
        setExpandedPillar(extracted.current_pillar);
      }
      reconcileReopened(prevPillars, nextPillars);    // drop verdicts for reopened pillars
      fireJudgesForNewLocks(prevPillars, nextPillars); // judge newly-locked pillars (background)
    }

    // Completion barrier (invariant #2): never finish while a judge is still
    // running or a critique is unresolved. If something is outstanding, reopen it
    // instead of advancing to approval — the tenant just gets another question.
    // Runs in testMode too, so admin test runs actually exercise the gate.
    if (complete) {
      await settlePendingJudges();
      const outstanding = gatherCritique();
      if (outstanding.length) {
        // Reuse this turn's user-last history so Surf can respond again; callChatApi
        // auto-attaches the pending critique and Surf reopens the gap.
        let reopenResp = null;
        try { reopenResp = await callChatApi(messageHistory); } catch (_) { /* fall through to gate */ }
        if (reopenResp) {
          const prev2 = pillarsRef.current;
          const next2 = reopenResp.extracted?.pillars || null;
          if (next2) {
            pillarsRef.current = next2;
            setPillars(next2);
            if (reopenResp.extracted.current_pillar) {
              setCurrentPillar(reopenResp.extracted.current_pillar);
              setExpandedPillar(reopenResp.extracted.current_pillar);
            }
            reconcileReopened(prev2, next2);
            fireJudgesForNewLocks(prev2, next2);
          }
          await streamAssistant(reopenResp.message, JSON.stringify(reopenResp));
          if (!reopenResp.complete) {
            // Surf reopened the gap — back to the conversation for the answer.
            setFoxState('idle');
            setIsFoxTyping(false);
            return;
          }
          // Surf re-completed despite the directive — stop fighting it and let the
          // server finalize gate have the final word rather than stranding the tenant.
          if (testMode) { setPendingTestExtracted(reopenResp.extracted); return; }
          await sleep(READING_DWELL_MS);
          await enterApprovalPhase();
          return;
        }
        // Reopen attempt failed — fall through; the server finalize gate backstops.
      }
    }

    // Normal path: stream Surf's message.
    await streamAssistant(message, JSON.stringify(chatResp));

    if (complete) {
      if (testMode) {
        console.log('[fox v2 test] complete=true, pillars:', JSON.stringify(extracted.pillars, null, 2));
        setPendingTestExtracted(extracted);
        return;
      }
      await sleep(READING_DWELL_MS);
      await enterApprovalPhase();
    }
  }

  // ─── Approval phase ───────────────────────────────────────────────────────
  async function enterApprovalPhase(pillarsOverride) {
    // DO NOT clear the recovery draft here. The conversation is done, but finalize
    // can still fail (bad area code, expired session, etc.) — if we wipe the draft
    // now, any finalize failure forces a full re-interview (this burned a real
    // tenant). The draft is cleared only after finalize SUCCEEDS (see runFinalize).
    setPhase('approval');
    setTalkTrackLoading(true);
    setTalkTrackError(null);
    const result = await callRegenerateTalkTrack(null, pillarsOverride);
    setTalkTrackLoading(false);
    if (result.ok && result.body?.strategy_block) {
      setTalkTrackDraft({
        strategy_block:            result.body.strategy_block,
        objectives_block:          result.body.objectives_block,
        sample_opener:             result.body.sample_opener,
        sample_qualification_flow: result.body.sample_qualification_flow,
      });
    } else {
      console.error('[fox v2] initial talk-track gen failed', result);
      setTalkTrackError(result.body?.message || 'Surf could not draft the talk track. Try refreshing.');
    }
  }

  async function handleFeedbackSubmit() {
    const feedback = feedbackInput.trim();
    if (!feedback) return;
    setFeedbackInput('');
    setTalkTrackLoading(true);
    setTalkTrackError(null);
    const result = await callRegenerateTalkTrack(feedback);
    setTalkTrackLoading(false);
    if (result.ok && result.body?.strategy_block) {
      setTalkTrackDraft({
        strategy_block:            result.body.strategy_block,
        objectives_block:          result.body.objectives_block,
        sample_opener:             result.body.sample_opener,
        sample_qualification_flow: result.body.sample_qualification_flow,
      });
      setFeedbackLog((prev) => [
        ...prev,
        { feedback, regenerated_at: new Date().toISOString() },
      ]);
    } else {
      setTalkTrackError(result.body?.message || 'Refinement failed. Try again.');
    }
  }

  function handleEditStart(blockKey) {
    setEditingBlock(blockKey);
    setEditingValue(talkTrackDraft?.[blockKey] || '');
  }

  function handleEditSave() {
    if (!editingBlock) return;
    setTalkTrackDraft((prev) => ({ ...prev, [editingBlock]: editingValue }));
    setEditingBlock(null);
    setEditingValue('');
  }

  function handleEditCancel() {
    setEditingBlock(null);
    setEditingValue('');
  }

  // ─── Approval → A2P gate → activation ─────────────────────────────────────
  // Approving the talk track no longer finalizes directly. First we route the
  // tenant through the required A2P carrier-registration step; only once their
  // customer profile is submitted do we run the activation ritual + finalize.
  async function handleApprove() {
    if (!talkTrackDraft) return;
    // Already registered (we're back here only from an area-code finalize retry):
    // skip the A2P step and re-run finalize directly.
    if (a2pSubmittedRef.current) {
      setPhase('activation');
      runFinalize();
      return;
    }
    // ── Test-tenant bypass ──────────────────────────────────────────────────
    // A designated tester account (a friend with no real business, signed up so
    // we can walk a fresh tenant through the real onboarding UX) skips carrier
    // registration entirely. Exact email match only — no effect on any real
    // tenant. The server /v2/finalize applies the matching trial-path override
    // so number provisioning still succeeds without a dedicated subaccount.
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (isA2PBypassEmail(user?.email)) {
        setPhase('activation');
        runFinalize();
        return;
      }
    } catch (_) { /* fall through to the normal A2P registration flow */ }
    // Durable skip: if this tenant already filed their carrier profile in a prior
    // session (e.g. they're redoing onboarding after a failed finalize), don't make
    // them sit through the registration form again. Show a one-click confirmation.
    // Fail-safe: any error falls through to the normal A2P registration flow.
    try {
      let tid = tenantId;
      if (!tid) {
        const { data: { user } } = await supabase.auth.getUser();
        tid = user?.app_metadata?.tenant_id;
      }
      if (tid) {
        const { data: existingProfile } = await supabase
          .from('customer_profiles')
          .select('id')
          .eq('tenant_id', tid)
          .maybeSingle();
        if (existingProfile) {
          setPhase('a2p_done');
          return;
        }
      }
    } catch (_) { /* fall through to the normal A2P registration flow */ }
    // Conversational hand-off: dismiss the talk-track overlay, let Surf say one
    // line in the chat, THEN slide up the A2P card — so it reads as Surf
    // switching topics rather than a form appearing out of nowhere.
    setPhase('a2p_handoff');
    setA2pHandoffReady(false);
    await streamAssistant(
      "Love it — that's your AI's game plan locked in. One last thing before you go live: I need to register your business with the carriers so your texts actually get delivered and don't get flagged. Takes about two minutes, right here.",
      null,
      { transient: true, endState: 'idle' }
    );
    // Don't auto-advance — let the tenant finish reading at their own pace and
    // open the card with an explicit "Let's go" (a deliberate "I'm ready to
    // register" beat before the form).
    setA2pHandoffReady(true);
  }

  // Fired by A2PCaptureStep once the subaccount + customer profile are submitted.
  function handleA2PSubmitted() {
    a2pSubmittedRef.current = true;
    setPhase('activation');
    runFinalize();
  }

  async function runFinalize(extraOverrides = {}) {
    if (finalizeFiredRef.current) return;
    finalizeFiredRef.current = true;
    setActivationError(null);
    const result = await callFinalizeApi(extraOverrides);
    if (!result.ok) {
      const code = result.body?.error;
      if (code === 'PHONE_AREA_CODE_UNAVAILABLE' || code === 'PHONE_PROVISIONING_FAILED') {
        finalizeFiredRef.current = false;
        setPhoneRetryMode(true);
        setPhase('approval');
        setActivationError(
          result.body?.message ||
            `Couldn't get a number with area code ${pillars.business_operations.data.area_code}. Pick another and we'll try again.`
        );
        return;
      }
      // Server-authoritative quality gate (invariant #2 backstop). Should almost
      // never fire because the client completion barrier already resolved per-pillar
      // gaps; it mainly catches cross-pillar coherence. Reopen with the verdict so
      // Surf fills the gaps, bounded by a retry cap to avoid a bounce loop.
      if (result.status === 422 && code === 'QUALITY_GATE_FAILED') {
        finalizeFiredRef.current = false;
        finalizeGateRetriesRef.current += 1;
        const v = result.body?.verdict || {};
        const crit = [
          ...(v.pillars || []).map((p) => ({ pillar: p.pillar, directive: p.directive || (p.gaps || []).map((g) => g.problem).join('; ') })),
          ...(v.cross_pillar || []).map((c) => ({ pillar: 'overall', directive: c.directive || c.problem })),
        ].filter((c) => c.directive);
        if (finalizeGateRetriesRef.current > 2 || !crit.length) {
          setPhase('approval');
          setActivationError('A few details still need more before we can build this. Add a little detail with Surf, then re-approve.');
          return;
        }
        // Persist per-pillar gaps in the ledger so later turns keep carrying them.
        (v.pillars || []).forEach((p) => {
          if (p.pillar && PILLAR_KEYS.includes(p.pillar)) {
            judgeLedgerRef.current[p.pillar] = { state: 'needs_clarity', directive: p.directive || '' };
          }
        });
        setPhase('conversation');
        setFoxState('thinking');
        setIsFoxTyping(true);
        let resp = null;
        try { resp = await callChatApi(historyThroughLastUser(), crit); } catch (_) { /* surfaced below */ }
        if (resp) {
          const prev = pillarsRef.current;
          const next = resp.extracted?.pillars || null;
          if (next) {
            pillarsRef.current = next;
            setPillars(next);
            if (resp.extracted.current_pillar) {
              setCurrentPillar(resp.extracted.current_pillar);
              setExpandedPillar(resp.extracted.current_pillar);
            }
            reconcileReopened(prev, next);
            fireJudgesForNewLocks(prev, next);
          }
          await streamAssistant(resp.message, JSON.stringify(resp));
        } else {
          setActivationError('Setup hit a snag. Please refresh and try again.');
        }
        setFoxState('idle');
        setIsFoxTyping(false);
        return;
      }
      console.error('[fox v2] finalize failed (non-recoverable)', result);
      finalizeFiredRef.current = false;
      setPhase('approval');
      setActivationError('Setup hit a snag. Please refresh and try again.');
      return;
    }

    // Success — NOW it's safe to clear the recovery draft (their setup is saved
    // server-side). Let the activation ritual run to completion, then navigate.
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
  }

  async function handleAreaCodeRetry(newAreaCode) {
    const digits = (newAreaCode || '').replace(/\D/g, '').slice(0, 3);
    if (digits.length !== 3) return;
    const newPillars = {
      ...pillars,
      business_operations: {
        ...pillars.business_operations,
        data: { ...pillars.business_operations.data, area_code: digits },
      },
    };
    setPillars(newPillars);
    setPhoneRetryMode(false);
    setActivationError(null);
    setPhase('activation');
    // runFinalize closes over `pillars` — pass the new value explicitly so
    // we don't depend on React state having flushed.
    finalizeFiredRef.current = false;
    const result = await fetchWithCsrf('/api/onboarding/v2/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pillars: newPillars,
        approved_talk_track: talkTrackDraft,
        feedback_log: feedbackLog,
        messages: (messagesRef.current || []).map((m) => ({ role: m.role, text: m.text, raw: m.raw || null })),
        started_at: startedAtRef.current || null,
        website_intel: websiteIntel || undefined,
        qc_ledger: qcHistoryRef.current,
      }),
    }).then((r) => r.json().then((body) => ({ ok: r.ok, body })));
    if (!result.ok) {
      const code = result.body?.error;
      if (code === 'PHONE_AREA_CODE_UNAVAILABLE' || code === 'PHONE_PROVISIONING_FAILED') {
        setPhoneRetryMode(true);
        setPhase('approval');
        setActivationError(result.body?.message || `Still no luck with ${digits}. Try another?`);
        return;
      }
      setPhase('approval');
      setActivationError('Setup hit a snag. Please refresh.');
    }
  }

  // ─── User submit (conversation) ───────────────────────────────────────────
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || !canSend) return;

    // Drop any transient status bubbles (squirrel / error notices) so they never
    // reach the model or the saved draft.
    const newHistory = [...messages.filter((m) => !m.transient), { role: 'user', text }];
    setMessages(newHistory);
    setInput('');
    await runChatTurn(newHistory);
  }

  // ─── Admin test handlers ─────────────────────────────────────────────────
  async function handleTestWrite() {
    if (!pendingTestExtracted) return;
    setPendingTestExtracted(null);
    await sleep(READING_DWELL_MS);
    await enterApprovalPhase();
  }

  function handleTestDiscard() {
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, raw: null, streaming: false }]);
    setPillars(DEFAULT_PILLAR_SHAPE);
    setCurrentPillar('business_operations');
    setExpandedPillar('business_operations');
    setWebsiteIntel(null);
    setWebsiteUrl(null);
    setScrapeStatus('idle');
    lastScrapedUrlRef.current = null;
    setInput('');
    setIsFoxTyping(false);
    setFoxState('idle');
    setPendingTestExtracted(null);
    setTalkTrackDraft(null);
    setFeedbackLog([]);
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-slate-950 text-slate-100 overflow-hidden">
      <style>{INLINE_STYLES}</style>
      <LeftPanel foxState={foxState} showFox={phase !== 'intro'} />
      <ChatPanel
        messages={messages}
        isFoxTyping={isFoxTyping}
        input={input}
        setInput={setInput}
        onSubmit={handleSubmit}
        canSend={canSend}
        chatScrollRef={chatScrollRef}
        inputRef={inputRef}
        foxState={foxState}
        phase={phase}
        retryPending={!!retryHistory}
        onRetry={() => {
          const h = retryHistory;
          setRetryHistory(null);
          setMessages((prev) => prev.filter((m) => !m.transient)); // clear status bubbles from view
          runChatTurn(h);
        }}
      />
      <PillarStack
        pillars={pillars}
        currentPillar={currentPillar}
        expandedPillar={expandedPillar}
        setExpandedPillar={setExpandedPillar}
        scrapeStatus={scrapeStatus}
        websiteUrl={websiteUrl}
        phase={phase}
      />
      {phase === 'approval' && (
        <TalkTrackPreview
          draft={talkTrackDraft}
          loading={talkTrackLoading}
          error={talkTrackError}
          feedbackInput={feedbackInput}
          setFeedbackInput={setFeedbackInput}
          onFeedbackSubmit={handleFeedbackSubmit}
          editingBlock={editingBlock}
          editingValue={editingValue}
          setEditingValue={setEditingValue}
          onEditStart={handleEditStart}
          onEditSave={handleEditSave}
          onEditCancel={handleEditCancel}
          onApprove={handleApprove}
          phoneRetryMode={phoneRetryMode}
          onAreaCodeRetry={handleAreaCodeRetry}
        />
      )}
      {phase === 'a2p_handoff' && a2pHandoffReady && (
        <div className="fixed inset-x-0 bottom-28 z-40 flex justify-center px-4 pointer-events-none">
          <button
            onClick={() => setPhase('a2p')}
            className="pointer-events-auto flex items-center gap-2 px-7 py-3 rounded-full bg-cyan-500 text-slate-950 text-sm font-bold shadow-[0_12px_30px_-6px_rgba(34,211,238,0.5)] hover:bg-cyan-400 transition-colors motion-safe:animate-sfPanelIn"
          >
            Let’s go <ArrowRight size={16} />
          </button>
        </div>
      )}
      {phase === 'a2p' && (
        <A2PCaptureStep
          pillars={pillars}
          onSuccess={handleA2PSubmitted}
        />
      )}
      {phase === 'a2p_done' && (
        <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-900/30 border border-emerald-700/50 mb-5">
              <ShieldCheck size={28} className="text-emerald-300" />
            </div>
            <div className="text-xl font-semibold text-slate-100 mb-2">You&apos;re already registered with the carriers</div>
            <div className="text-sm text-slate-400 leading-relaxed mb-7">
              Your carrier registration is already filed and under review. No need to redo it. We&apos;ll let you know the moment you&apos;re cleared to send.
            </div>
            <button
              onClick={handleA2PSubmitted}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 transition-colors"
            >
              <Check size={16} /> Finish setup
            </button>
          </div>
        </div>
      )}
      {phase === 'activation' && (
        <ActivationRitual
          error={activationError}
          aiName={pillars.voice_guardrails?.data?.ai_name || null}
          onComplete={() => {
            navigate('/dashboard');
          }}
        />
      )}
      {pendingTestExtracted && (
        <AdminTestReviewOverlay
          pillars={pendingTestExtracted.pillars}
          onWrite={handleTestWrite}
          onDiscard={handleTestDiscard}
        />
      )}
      {guardChecked && phase === 'intro' && (
        <IntroFade
          onStart={() => setPhase('conversation')}
          onDefer={async () => {
            // No platform access until onboarding is complete. "Come back later"
            // signs the user out rather than dropping them into a gated app they
            // would only get bounced out of. They resume onboarding on next login.
            try { await supabase.auth.signOut(); } catch (_) { /* sign out best-effort */ }
            navigate('/login');
          }}
        />
      )}
    </div>
  );
}

// ─── Shared CSS (copied from v1 to keep v2 fully independent) ──────────────
const INLINE_STYLES = `
@keyframes fox-float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
@keyframes particle-rise { 0% { transform: translateY(0); opacity: 0; } 25% { opacity: 0.75; } 100% { transform: translateY(-140px); opacity: 0; } }
@keyframes inscribe-text { from { clip-path: inset(0 100% 0 0); -webkit-clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); -webkit-clip-path: inset(0 0 0 0); } }
@keyframes thinking-edge { 0%,100% { opacity: 0.35; } 50% { opacity: 0.95; } }
@keyframes overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes glow-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.55; } }
@keyframes activation-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.fox-float { animation: fox-float 6s ease-in-out infinite; }
.particle { animation: particle-rise linear infinite; }
.inscribe-text { animation: inscribe-text 700ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.thinking-edge { animation: thinking-edge 2.2s ease-in-out infinite; }
.overlay-in { animation: overlay-in 500ms ease-out forwards; }
.glow-idle { animation: glow-pulse 4s ease-in-out infinite; }
.activation-rise { animation: activation-rise 1000ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
`;

// ─── Left panel ─────────────────────────────────────────────────────────────
function LeftPanel({ foxState, showFox = true }) {
  return (
    <div className="relative flex-shrink-0 w-[32%] min-w-[320px] h-full bg-slate-950 overflow-hidden border-r border-slate-800/70">
      <HexBackground />
      <Particles />
      <div className="absolute top-6 left-6 flex items-center gap-2 z-10">
        <img src="/logo.png" alt="SurFox AI" className="w-8 h-8 object-contain" />
        <span className="font-semibold tracking-wide text-slate-200">SurFox AI</span>
      </div>
      <div className="absolute inset-x-0 top-0 bottom-12 flex items-center justify-center">
        {showFox && <FoxAsset state={foxState} />}
      </div>
      <div className="absolute bottom-6 left-0 right-0 text-center text-[11px] text-cyan-400/80 tracking-[0.35em] uppercase font-medium">
        Surf · your setup guide
      </div>
    </div>
  );
}

function FoxAsset({ state }) {
  const isThinking = state === 'thinking';
  const isSpeaking = state === 'speaking';
  const isDistracted = state === 'distracted';

  const glowMain = isSpeaking
    ? 'opacity-100 scale-125'
    : isThinking
      ? 'opacity-80 scale-110'
      : isDistracted
        ? 'opacity-65 scale-95'
        : 'opacity-45 scale-100';

  const glowOuter = isDistracted ? 'bg-amber-400' : 'bg-cyan-500';
  const glowInner = isDistracted ? 'bg-amber-300' : 'bg-cyan-300';
  const dropShadow = isDistracted
    ? 'drop-shadow(0 0 12px rgba(251, 191, 36, 0.55))'
    : 'drop-shadow(0 0 12px rgba(0, 200, 180, 0.45))';

  return (
    <div className="relative w-full h-full">
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[420px] h-40 rounded-full ${glowOuter} blur-[80px] transition-all duration-700 ease-out pointer-events-none ${glowMain} ${state === 'idle' ? 'glow-idle' : ''}`} />
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 w-52 h-24 rounded-full ${glowInner} blur-3xl transition-all duration-500 pointer-events-none ${glowMain}`} />
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent pointer-events-none" />
      <div className="absolute inset-0 flex justify-center items-end pointer-events-none">
        <img
          src="/full_body_mirrored.png"
          alt="Surf"
          className={`h-auto max-h-full w-auto max-w-full object-contain ${isDistracted ? '' : 'fox-float'}`}
          style={{
            filter: dropShadow,
            transform: isDistracted ? 'rotate(-3deg)' : undefined,
            transition: 'filter 500ms ease, transform 500ms ease',
          }}
        />
      </div>
    </div>
  );
}

function HexBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full text-cyan-500/15 pointer-events-none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="fox2-hex-grid" width="48" height="42" patternUnits="userSpaceOnUse">
          <path d="M12 0 L36 0 L48 21 L36 42 L12 42 L0 21 Z" fill="none" stroke="currentColor" strokeWidth="0.6" />
        </pattern>
        <radialGradient id="fox2-hex-fade" cx="50%" cy="50%" r="65%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id="fox2-hex-mask">
          <rect width="100%" height="100%" fill="url(#fox2-hex-fade)" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="url(#fox2-hex-grid)" mask="url(#fox2-hex-mask)" />
    </svg>
  );
}

function Particles() {
  const items = Array.from({ length: 18 }, (_, i) => ({
    left: (i * 37) % 100,
    bottom: -10 + ((i * 17) % 30),
    delay: (i * 0.5) % 6,
    dur: 7 + (i % 5),
    size: i % 3 === 0 ? 2 : 1,
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {items.map((p, i) => (
        <div
          key={i}
          className="particle absolute rounded-full bg-cyan-300/60"
          style={{
            left: `${p.left}%`,
            bottom: `${p.bottom}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Center chat ────────────────────────────────────────────────────────────
function ChatPanel({ messages, isFoxTyping, input, setInput, onSubmit, canSend, chatScrollRef, inputRef, foxState, phase, retryPending, onRetry }) {
  const isThinking = foxState === 'thinking';
  const isApproval = phase === 'approval';
  return (
    <div className="relative flex-1 flex flex-col h-full bg-slate-900/80 min-w-0">
      <div
        className={`absolute top-0 left-0 right-0 h-px pointer-events-none transition-colors duration-500 ${
          isThinking ? 'bg-cyan-500/40' : 'bg-transparent'
        }`}
      />
      <div className="relative px-8 py-5 border-b border-slate-800 flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${canSend ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'}`} />
        <div className="text-sm font-medium text-slate-300">
          {isApproval ? 'Review your AI' : 'Setup conversation'}
        </div>
      </div>
      <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
        {messages.map((m, i) => (
          <ChatMessage key={i} message={m} />
        ))}
        {isFoxTyping && <TypingIndicator />}
      </div>
      <div className="px-8 py-5 border-t border-slate-800">
        {retryPending && (
          <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5">
            <div className="text-xs text-amber-200 mb-2">
              Hit a snag reaching Surf. Your progress is saved. Tap Retry to try again now, or reload and pick up right here later if it keeps failing.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onRetry}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-400 text-slate-950 hover:bg-amber-300 transition-colors"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-transparent border border-amber-500/50 text-amber-200 hover:bg-amber-500/20 transition-colors"
              >
                Reload &amp; resume
              </button>
            </div>
          </div>
        )}
        <form
          onSubmit={onSubmit}
          className={`flex items-center gap-3 bg-slate-800/60 border rounded-xl px-4 py-2.5 transition-colors ${
            canSend ? 'border-slate-700 focus-within:border-cyan-500/60' : 'border-slate-800 opacity-60'
          }`}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={canSend ? 'Type your answer...' : isApproval ? 'Reviewing your AI...' : 'Surf is thinking...'}
            disabled={!canSend}
            className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none disabled:cursor-not-allowed"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!canSend || !input.trim()}
            className="p-2 rounded-lg bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  if (message.role === 'assistant') {
    return (
      <div className="flex items-start gap-3 max-w-[88%]">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
        <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3 text-slate-100 leading-relaxed">
          <span>{message.text}</span>
          {message.streaming && (
            <span className="inline-block w-1.5 h-4 bg-cyan-400 ml-1 align-middle animate-pulse" />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="rounded-2xl rounded-tr-sm bg-cyan-500/15 border border-cyan-500/30 px-4 py-3 text-slate-100 max-w-[80%] leading-relaxed">
        {message.text}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
      <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3">
        <div className="flex gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '160ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '320ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Right panel: pillar stack ──────────────────────────────────────────────
// Vertical stack of 7 cards. Each card shows status + summary; the
// currently-active pillar auto-expands and renders its captured data.
// Plus a tiny "Reading {url}" line at the top while the scrape is in flight.
function PillarStack({ pillars, currentPillar, expandedPillar, setExpandedPillar, scrapeStatus, websiteUrl, phase }) {
  const lockedCount = PILLAR_KEYS.filter((k) => pillars[k]?.status === 'locked').length;
  const total = PILLAR_KEYS.length;
  return (
    <div className="relative flex-shrink-0 w-[28%] min-w-[320px] h-full bg-slate-950 flex flex-col">
      <div className="absolute inset-y-0 left-0 w-px bg-cyan-500/25 pointer-events-none z-10" />
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-cyan-500/[0.04] to-transparent pointer-events-none" />

      <div className="relative px-6 py-5 border-b border-slate-800">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
          {phase === 'approval' ? 'Strategy Preview' : 'Pillars'}
        </div>
        <div className="text-sm text-slate-400">
          {phase === 'approval' ? 'Surf drafted your talk track' : 'What I\'m learning about your business'}
        </div>

        {/* Progress — keep the finish line visible so length never feels open-ended. */}
        {phase !== 'approval' && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-slate-500">{lockedCount} of {total} locked in</span>
              {lockedCount === total
                ? <span className="text-cyan-400">all set</span>
                : lockedCount >= 5 ? <span className="text-cyan-400">almost there</span>
                : lockedCount >= 4 ? <span className="text-cyan-400/80">over halfway</span>
                : null}
            </div>
            <div className="h-1 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-cyan-500 transition-all duration-500 ease-out"
                style={{ width: `${(lockedCount / total) * 100}%` }}
              />
            </div>
          </div>
        )}

        <ScrapeIndicator scrapeStatus={scrapeStatus} websiteUrl={websiteUrl} />
      </div>

      <div className="relative flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {PILLAR_KEYS.map((key) => (
          <PillarCard
            key={key}
            pillarKey={key}
            pillar={pillars[key]}
            isCurrent={key === currentPillar}
            isExpanded={key === expandedPillar}
            onToggle={() => setExpandedPillar(expandedPillar === key ? null : key)}
          />
        ))}
      </div>
    </div>
  );
}

function ScrapeIndicator({ scrapeStatus, websiteUrl }) {
  if (scrapeStatus === 'idle') return null;
  const shortUrl = (websiteUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return (
    <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
      {scrapeStatus === 'scraping' && (
        <>
          <Loader2 size={11} className="animate-spin text-cyan-400" />
          <span>Reading <span className="text-cyan-300">{shortUrl}</span>...</span>
        </>
      )}
      {scrapeStatus === 'done' && (
        <>
          <Check size={11} className="text-cyan-400" />
          <span>Read <span className="text-cyan-300">{shortUrl}</span></span>
        </>
      )}
      {scrapeStatus === 'failed' && (
        <span className="text-slate-600">Couldn't read site, continuing without it</span>
      )}
    </div>
  );
}

function PillarCard({ pillarKey, pillar, isCurrent, isExpanded, onToggle }) {
  const { status, confidence, data } = pillar;
  const isLocked = status === 'locked';
  const isInProgress = status === 'in_progress';

  const statusIcon = isLocked
    ? <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center shadow-md shadow-cyan-500/30"><Check size={12} className="text-slate-950" strokeWidth={3} /></div>
    : isInProgress
      ? <div className="w-5 h-5 rounded-full bg-slate-800 border border-cyan-500/60 flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" /></div>
      : <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700" />;

  const summary = pillarSummary(pillarKey, data);

  return (
    <div
      onClick={onToggle}
      className={`relative rounded-lg border transition-all cursor-pointer ${
        isCurrent
          ? 'border-cyan-500/40 bg-slate-900/80 shadow-lg shadow-cyan-500/5'
          : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-shrink-0 mt-0.5">{statusIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-slate-200">{PILLAR_DISPLAY[pillarKey]}</div>
            {confidence > 0 && !isLocked && (
              <div className="text-[10px] text-slate-500 flex-shrink-0">{confidence}%</div>
            )}
          </div>
          {!isExpanded && summary && (
            <div className="text-[11px] text-slate-500 mt-1 line-clamp-1">{summary}</div>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-3 pt-1 space-y-1.5 border-t border-slate-800/60 mt-1">
          <PillarDataView pillarKey={pillarKey} data={data} />
        </div>
      )}
    </div>
  );
}

// One-line per-pillar summary used in the collapsed card view.
function pillarSummary(key, data) {
  if (!data) return '';
  if (key === 'business_operations') {
    return data.company_name || data.what_they_sell || '';
  }
  if (key === 'industry_context') {
    return data.industry_vocabulary?.length ? `${data.industry_vocabulary.length} terms` : '';
  }
  if (key === 'audience') return data.icp_description || '';
  if (key === 'qualification') {
    const parts = [];
    if (data.fit_signals?.length) parts.push(`${data.fit_signals.length} fit signals`);
    if (data.qualifying_questions?.length) parts.push(`${data.qualifying_questions.length} questions`);
    return parts.join(', ');
  }
  if (key === 'voice_guardrails') {
    return [data.tone, data.persona].filter(Boolean).join(' · ');
  }
  if (key === 'objections') {
    return data.top_objections?.length ? `${data.top_objections.length} objections` : '';
  }
  if (key === 'conversion_goal') return data.primary_goal || '';
  return '';
}

function PillarDataView({ pillarKey, data }) {
  // Render the captured fields in a compact label:value list. Skip empties.
  const rows = [];
  const push = (label, value) => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && !value.trim()) return;
    if (Array.isArray(value) && value.length === 0) return;
    rows.push({ label, value });
  };

  if (pillarKey === 'business_operations') {
    push('Company',         data.company_name);
    push('Contact',         data.contact_first_name);
    push('Website',         data.website);
    push('Industry',        data.industry);
    push('Sells',           data.what_they_sell);
    push('Differentiators', data.differentiators);
    push('Lead handler',    data.lead_handler_today);
    push('Sales cycle',     data.sales_cycle_rough);
    push('Area code',       data.area_code);
    push('Hours',           data.hours_of_operation);
    push('Geography',       data.geographic_coverage);
  }
  if (pillarKey === 'industry_context') {
    push('Vocabulary',  data.industry_vocabulary);
    push('Landmines',   data.industry_landmines);
    push('Signals',     data.buying_signals);
    push('Regulatory',  data.regulatory_considerations);
  }
  if (pillarKey === 'audience') {
    push('ICP',         data.icp_description);
    push('Channel',     data.acquisition_channel);
    push('Awareness',   data.awareness_level);
    push('Motivation',  data.lead_motivation);
    push('Known at intake', data.known_intake_fields);
    push('Sub-segments',data.audience_sub_segments);
  }
  if (pillarKey === 'qualification') {
    push('Must-haves',   data.hard_requirements);
    push('Fit signals',  data.fit_signals);
    push('Disqualifiers',data.disqualifiers);
    push('Questions',    data.qualifying_questions);
    push('Escalation',   data.escalation_trigger);
  }
  if (pillarKey === 'voice_guardrails') {
    push('AI name',      data.ai_name);
    push('Tone',         data.tone);
    push('Persona',      data.persona);
    push('Voice',        data.brand_voice_descriptors);
    push('Guardrails',   data.custom_guardrails);
  }
  if (pillarKey === 'objections') {
    if (data.top_objections?.length) {
      data.top_objections.forEach((o, i) => {
        push(`Objection ${i+1}`, o.objection);
      });
    }
  }
  if (pillarKey === 'conversion_goal') {
    push('Goal',       data.primary_goal);
    push('Books',      data.meeting_modality === 'in_person' ? 'An in-person meeting (time and place)'
                     : data.meeting_modality === 'phone'     ? 'A phone call'
                     : '');
    push('Escalation', data.escalation_trigger);
    push('Success',    data.success_criteria);
  }

  if (rows.length === 0) {
    return <div className="text-[11px] text-slate-600 italic">Surf hasn't gathered anything here yet.</div>;
  }

  return (
    <>
      {rows.map((r) => (
        <div key={r.label} className="flex gap-2 text-[11px]">
          <div className="text-slate-500 flex-shrink-0 w-24">{r.label}</div>
          <div className="text-slate-300 break-words flex-1">
            {Array.isArray(r.value) ? r.value.join(', ') : r.value}
          </div>
        </div>
      ))}
    </>
  );
}

// ─── Talk-track approval overlay ───────────────────────────────────────────
// Exported so the in-platform SurfWidget can reuse the exact same approval
// experience for create_campaign without duplicating component code.
export function TalkTrackPreview({
  draft, loading, error,
  feedbackInput, setFeedbackInput, onFeedbackSubmit,
  editingBlock, editingValue, setEditingValue, onEditStart, onEditSave, onEditCancel,
  onApprove,
  phoneRetryMode, onAreaCodeRetry,
}) {
  const [areaCodeInput, setAreaCodeInput] = useState('');

  return (
    <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6 py-8 overflow-y-auto">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10 my-auto">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-2">Your talk track</div>
          <div className="text-2xl font-semibold text-slate-100">Read this and tell me if it sounds right.</div>
          <div className="text-xs text-slate-500 mt-2 max-w-xl mx-auto leading-relaxed">
            This is exactly how your AI will approach leads. Approve it, edit any part inline, or tell me what to change in plain English at the bottom.
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={28} className="animate-spin text-cyan-400" />
            <div className="text-sm text-slate-400">Surf is drafting your talk track...</div>
          </div>
        )}

        {error && !loading && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-red-900/30 border border-red-700/60 text-sm text-red-200">
            {error}
          </div>
        )}

        {phoneRetryMode && (
          <div className="mb-6 p-4 rounded-lg bg-amber-900/20 border border-amber-700/40">
            <div className="text-sm text-amber-200 mb-3">
              No SurFox numbers available in that area code. Try another:
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={areaCodeInput}
                onChange={(e) => setAreaCodeInput(e.target.value)}
                placeholder="3-digit area code"
                className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 text-sm placeholder-slate-500"
                maxLength={3}
              />
              <button
                onClick={() => onAreaCodeRetry(areaCodeInput)}
                className="px-4 py-2 rounded bg-cyan-500 text-slate-950 text-sm font-medium hover:bg-cyan-400"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {!loading && draft && (
          <>
            <TalkTrackBlock
              label="Strategy"
              blockKey="strategy_block"
              value={draft.strategy_block}
              editing={editingBlock === 'strategy_block'}
              editingValue={editingValue}
              setEditingValue={setEditingValue}
              onEditStart={onEditStart}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
            />
            <TalkTrackBlock
              label="Sample opener"
              blockKey="sample_opener"
              value={draft.sample_opener}
              editing={editingBlock === 'sample_opener'}
              editingValue={editingValue}
              setEditingValue={setEditingValue}
              onEditStart={onEditStart}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
              mono
            />
            <TalkTrackBlock
              label="Sample qualification flow"
              blockKey="sample_qualification_flow"
              value={draft.sample_qualification_flow}
              editing={editingBlock === 'sample_qualification_flow'}
              editingValue={editingValue}
              setEditingValue={setEditingValue}
              onEditStart={onEditStart}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
              mono
            />
            <TalkTrackBlock
              label="Conversation objectives"
              blockKey="objectives_block"
              value={draft.objectives_block}
              editing={editingBlock === 'objectives_block'}
              editingValue={editingValue}
              setEditingValue={setEditingValue}
              onEditStart={onEditStart}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
              mono
              collapsedByDefault
            />

            {/* Feedback input */}
            <div className="mt-6 p-4 rounded-lg bg-slate-800/40 border border-slate-700/60">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                Want to refine it?
              </div>
              <div className="text-xs text-slate-400 mb-3">
                Tell Surf what to change. Example: "less aggressive in the opener" or "don't push for a call before message 3"
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={feedbackInput}
                  onChange={(e) => setFeedbackInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onFeedbackSubmit(); }}
                  placeholder="What should be different?"
                  className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-100 text-sm placeholder-slate-500"
                />
                <button
                  onClick={onFeedbackSubmit}
                  disabled={!feedbackInput.trim()}
                  className="px-4 py-2 rounded bg-slate-700 text-slate-200 text-sm font-medium hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Sparkles size={14} /> Regenerate
                </button>
              </div>
            </div>

            {/* Approve button */}
            <div className="mt-8">
              <button
                onClick={onApprove}
                disabled={phoneRetryMode}
                className="w-full px-6 py-4 rounded-lg bg-cyan-500 text-slate-950 font-semibold text-sm hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                This is how I'd want it. Turn it on.
              </button>
              <p className="mt-3 text-center text-xs text-slate-500 leading-relaxed">
                Your AI brings you qualified, ready-to-talk leads. Closing them is your team's play, that part's on you.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TalkTrackBlock({ label, blockKey, value, editing, editingValue, setEditingValue, onEditStart, onEditSave, onEditCancel, mono = false, collapsedByDefault = false }) {
  const [collapsed, setCollapsed] = useState(collapsedByDefault);

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/70">{label}</div>
        <div className="flex items-center gap-2">
          {collapsedByDefault && !editing && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-[10px] text-slate-500 hover:text-slate-300"
            >
              {collapsed ? 'Show' : 'Hide'}
            </button>
          )}
          {!editing && (
            <button
              onClick={() => onEditStart(blockKey)}
              className="text-[10px] text-slate-500 hover:text-cyan-400 flex items-center gap-1"
            >
              <Edit2 size={11} /> Edit
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <>
          <textarea
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            rows={Math.max(6, Math.min(20, (editingValue.split('\n').length + 2)))}
            className={`w-full px-3 py-2 rounded bg-slate-800 border border-cyan-500/40 text-slate-100 text-sm focus:outline-none focus:border-cyan-400 ${mono ? 'font-mono text-xs' : ''}`}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={onEditSave}
              className="px-3 py-1.5 rounded bg-cyan-500 text-slate-950 text-xs font-medium hover:bg-cyan-400"
            >
              Save
            </button>
            <button
              onClick={onEditCancel}
              className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 text-xs hover:bg-slate-800"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        !collapsed && (
          <div className={`px-4 py-3 rounded bg-slate-800/60 border border-slate-700/40 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed ${mono ? 'font-mono text-xs' : ''}`}>
            {value}
          </div>
        )
      )}
    </div>
  );
}

// ─── Activation ritual ─────────────────────────────────────────────────────
// Earned ending. "Your AI is live." → beat → subline → beat → dashboard hop.
// While the animation runs, /v2/finalize is already in flight in the parent.
// onComplete fires once the animation completes; parent navigates.
function ActivationRitual({ error, aiName, onComplete }) {
  const [stage, setStage] = useState(0); // 0=glow rise, 1=primary, 2=subline, 3=fade

  useEffect(() => {
    if (error) return;
    const timers = [
      setTimeout(() => setStage(1), 800),
      setTimeout(() => setStage(2), 2400),
      setTimeout(() => setStage(3), 4800),
      setTimeout(() => onComplete?.(), 5800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [error, onComplete]);

  if (error) {
    // Activation failed — let parent handle by exiting back to approval phase.
    // We render a quiet error veil for one frame so the user isn't stuck on
    // a half-cinematic.
    return (
      <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-sm text-red-300 mb-3">{error}</div>
          <div className="text-xs text-slate-500">Returning you to the talk track...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 bg-slate-950 transition-opacity duration-1000 ${stage >= 3 ? 'opacity-0' : 'opacity-100'}`}>
      <HexBackground />
      <Particles />

      {/* Floor glow grows from 0 to 1 over the ritual */}
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-56 rounded-full bg-cyan-400 blur-[120px] pointer-events-none transition-all duration-[1500ms] ease-out ${
          stage === 0 ? 'opacity-0 scale-50' : 'opacity-90 scale-100'
        }`}
      />

      {/* Primary line */}
      <div className={`absolute inset-0 flex items-center justify-center ${stage >= 1 ? 'activation-rise' : 'opacity-0'}`}>
        <div className="text-5xl font-light text-slate-100 tracking-wide">
          {aiName ? `${aiName} is ready.` : 'Your AI is ready.'}
        </div>
      </div>

      {/* Subline appears under the primary */}
      <div className={`absolute inset-x-0 top-[56%] flex justify-center transition-opacity duration-700 ${stage >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        <div className="text-base text-slate-400 tracking-wide text-center px-8">
          Let's show you around.
        </div>
      </div>
    </div>
  );
}

// ─── Intro fade (copied from v1 — same animation) ──────────────────────────
function IntroFade({ onStart, onDefer }) {
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    const timers = [
      setTimeout(() => setBeat(1), 1300),
      setTimeout(() => setBeat(2), 4000),
      setTimeout(() => setBeat(3), 5500),
      // Beat 4 reveals the expectation gate instead of auto-starting the chat.
      // The tenant must explicitly commit before the interview begins — a
      // rushed onboarding produces a weak qualifier, and at self-serve scale
      // this is the only realistic quality guard before the AI goes live.
      setTimeout(() => setBeat(4), 7100),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const backdropVisible = beat < 3;
  const foxTransform = beat >= 3
    ? 'translateX(-34vw) translateY(32px) scale(1.25)'
    : 'translateX(0) translateY(0) scale(1)';

  return (
    <div className={`fixed inset-0 z-50 ${beat === 3 ? 'pointer-events-none' : ''}`}>
      <div className={`absolute inset-0 bg-slate-950 transition-opacity duration-[1200ms] ease-out ${backdropVisible ? 'opacity-100' : 'opacity-0'}`}>
        <HexBackground />
        <Particles />
        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-48 rounded-full bg-cyan-500 blur-[100px] pointer-events-none transition-all duration-[1400ms] ease-out ${beat === 0 ? 'opacity-0 scale-50' : 'opacity-70 scale-100'}`} />
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${beat === 0 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-4xl font-light text-slate-100 tracking-wide">Welcome in.</div>
        </div>
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${beat === 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-3xl font-light text-slate-200 tracking-wide">Setting up your AI.</div>
        </div>
        <div className={`absolute inset-x-0 bottom-16 flex justify-center transition-opacity duration-500 ${beat === 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-3xl font-light text-slate-100 tracking-wide">Meet Surf.</div>
        </div>
      </div>
      <div className="absolute inset-0 flex justify-center items-end pb-20 pointer-events-none">
        <div
          style={{
            transform: foxTransform,
            transformOrigin: 'center bottom',
            opacity: beat >= 2 ? 1 : 0,
            transition: 'transform 1500ms cubic-bezier(0.4, 0.0, 0.2, 1), opacity 600ms ease-out',
          }}
        >
          <img
            src="/full_body_mirrored.png"
            alt="Surf"
            className="h-[58vh] max-h-[480px] w-auto object-contain fox-float"
            style={{ filter: 'drop-shadow(0 0 24px rgba(0, 200, 180, 0.55))' }}
          />
        </div>
      </div>

      {/* Expectation gate — explicit commitment before the interview starts. */}
      {beat >= 4 && (
        <div className="overlay-in absolute inset-0 z-20 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6 pointer-events-auto">
          <div className="w-full max-w-md bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-2 text-center">Before we start</div>
            <div className="text-2xl font-semibold text-slate-100 text-center mb-4">Take your time on this</div>
            <div className="space-y-3 text-sm text-slate-300 leading-relaxed mb-7">
              <p>Your answers in this conversation shape how well your AI qualifies every lead it talks to. The more specific you are, the sharper it gets.</p>
              <p>Plan on about 20 minutes of focused attention. You don't have to do it all in one sitting, though, I save your spot automatically, so you can step away anytime and pick up right where you left off.</p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={onStart}
                className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-cyan-500 text-slate-950 hover:bg-cyan-400 transition-colors"
              >
                I've got 20 minutes, let's go
              </button>
              <button
                onClick={onDefer}
                className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-transparent border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
              >
                I'll come back later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin test review overlay (global_admin only) ─────────────────────────
// When the admin runs through onboarding in testMode and complete=true fires,
// pause here to let them inspect the locked pillars and decide whether to
// write to their tenant or discard and restart. Identical contract to v1.
function AdminTestReviewOverlay({ pillars, onWrite, onDiscard }) {
  const rows = PILLAR_KEYS.map((k) => ({
    key: k,
    label: PILLAR_DISPLAY[k],
    status: pillars[k]?.status,
    confidence: pillars[k]?.confidence,
    summary: pillarSummary(k, pillars[k]?.data),
  }));

  return (
    <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-2">Admin test review</div>
          <div className="text-2xl font-semibold text-slate-100">Ready to draft the talk track?</div>
          <div className="text-xs text-slate-500 mt-2">Pillars locked. Next: Surf drafts your talk track from this material.</div>
        </div>
        <div className="space-y-2 mb-7">
          {rows.map((r) => (
            <div key={r.key} className="flex items-baseline gap-3">
              <div className="flex-shrink-0 w-32 text-[10px] uppercase tracking-widest text-slate-500">{r.label}</div>
              <div className="flex-1 text-xs text-slate-300">
                <span className={`mr-2 ${r.status === 'locked' ? 'text-cyan-400' : 'text-slate-600'}`}>
                  {r.status === 'locked' ? '✓' : r.status === 'in_progress' ? '~' : '·'}
                </span>
                {r.summary || <span className="text-slate-600 italic">empty</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={onWrite}
            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-cyan-500 text-slate-950 hover:bg-cyan-400 transition-colors"
          >
            Continue to talk track draft
          </button>
          <button
            onClick={onDiscard}
            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-transparent border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            Discard and restart
          </button>
        </div>
      </div>
    </div>
  );
}
